// Pending Verifications detail page — full paginated list of users who
// haven't confirmed their email yet. Reached from the dashboard's
// "Pending Verifications" widget. Click-through drills into the user.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Paper, Pagination, Chip, CircularProgress, Alert, Stack,
    IconButton, Tooltip, Button,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import axiosInstance from '../utils/axiosInstance';
import { useDebounce } from '../utils/useDebounce';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 25;

const PendingVerifications = () => {
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const debouncedSearch = useDebounce(search, 350);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            const res = await axiosInstance.get('/admin/dashboard/pending-verifications', { params });
            setUsers(res.data?.users || []);
            setTotal(res.data?.total || 0);
            setPages(res.data?.pages || 1);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load pending verifications.');
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ fontFamily }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}
                    sx={{ fontFamily, textTransform: 'none', mb: 2 }}>
                Back to dashboard
            </Button>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                   alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Pending Verifications
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        Users who haven't completed email verification yet.
                    </Typography>
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    {total} pending
                </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                    size="small" label="Search email" value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    sx={{ minWidth: 260 }}
                />
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            ) : users.length === 0 ? (
                <Typography sx={{ fontFamily, color: '#64748b', py: 6, textAlign: 'center' }}>
                    No unverified users.
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                        <Table aria-label="pending verifications">
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>User ID</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Email</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Account</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Active</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((u) => (
                                    <TableRow key={u.user_id} hover>
                                        <TableCell sx={{ fontFamily, fontWeight: 600 }}>#{u.user_id}</TableCell>
                                        <TableCell sx={{ fontFamily }}>{u.user_email}</TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {u.account_type || '--'}
                                        </TableCell>
                                        <TableCell>
                                            {u.is_active
                                                ? <Chip size="small" color="success" label="Active" sx={{ fontFamily }} />
                                                : <Chip size="small" color="error" label="Inactive" sx={{ fontFamily }} />}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View detail">
                                                <IconButton size="small"
                                                            onClick={() => navigate(`/users/${u.user_id}`)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pages} page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary" size="small"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default PendingVerifications;
